import { Link } from "react-router-dom";
import { Instagram, Linkedin, Mail, Globe, Handshake, ArrowUpRight } from "lucide-react";
import { IMG, usePageContent } from "../lib/api";
import { PAGES } from "../content/pageContent";
import Logo from "./Logo";
import { Reveal, MaskedLine } from "./Shared";

const QUICK = [
  { to: "/about", label: "About Us" },
  { to: "/team", label: "Our Team" },
  { to: "/legacy", label: "Our Legacy" },
  { to: "/projects", label: "Projects" },
  { to: "/blog", label: "Blog" },
  { to: "/publications", label: "Publications" },
  { to: "/calendar", label: "Calendar" },
  { to: "/gallery", label: "Gallery" },
  { to: "/our-north-star", label: "Our North Star" },
  { to: "/rotary-club-of-bombay-central", label: "Rotary Club of Bombay Central" },
  { to: "/collaborators", label: "Collaborators & Sponsors" },
];

function getSocialHref(label, val) {
  if (!val || val.startsWith("[")) return null;
  if (label === "Instagram") {
    if (val.startsWith("http")) return val;
    const handle = val.replace(/^@/, "").trim();
    return `https://instagram.com/${handle}`;
  }
  if (label === "Email") {
    if (val.includes("@")) return `mailto:${val.trim()}`;
    return null;
  }
  if (label === "LinkedIn") {
    if (val.startsWith("http")) return val;
    return `https://linkedin.com/in/${val.trim()}`;
  }
  if (label === "College Website") {
    if (val.startsWith("http")) return val;
    return "https://sophiacollegemumbai.com";
  }
  if (val.startsWith("http")) return val;
  return null;
}

export default function Footer() {
  const c = usePageContent("contact", PAGES.contact.defaults);
  const SOCIALS = [
    { icon: Instagram, label: "Instagram", note: c.instagram },
    { icon: Linkedin, label: "LinkedIn", note: c.linkedin },
    { icon: Mail, label: "Email", note: c.email },
    { icon: Globe, label: "College Website", note: c.college },
    { icon: Handshake, label: "Rotary Connection", note: c.rotary },
  ];
  return (
    <footer className="relative overflow-hidden border-t border-silver/10" id="connect" data-testid="footer">
      <div className="absolute inset-0">
        <img src={IMG.celestialSky} alt="" className="h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/85 to-ink" />
      </div>
      <div className="relative z-10 mx-auto max-w-7xl px-6 py-20">
        <MaskedLine>
          <p className="eyebrow flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-ember" /> Connect With RCSCW
          </p>
        </MaskedLine>
        <div className="mt-8 grid gap-14 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <MaskedLine delay={0.1}>
              <h2 className="font-display text-4xl font-semibold leading-tight sm:text-5xl">
                <span className="text-silver">Rotaract Club of</span> <em className="metallic">Sophia College for Women</em>
              </h2>
            </MaskedLine>
            <Reveal delay={0.15}>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-silver-muted">
                Rotary Year 2026–27 · Forged: Reclaiming the Fire. Sponsored by the Rotary Club of Bombay Central, RID 3141.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 flex flex-wrap gap-3">
                {SOCIALS.map((s) => {
                  const href = getSocialHref(s.label, s.note);
                  return (
                    <a
                      key={s.label}
                      href={href || "#"}
                      target={href && !href.startsWith("mailto:") ? "_blank" : undefined}
                      rel={href && !href.startsWith("mailto:") ? "noopener noreferrer" : undefined}
                      onClick={href ? undefined : (e) => e.preventDefault()}
                      className="card-line group flex items-center gap-3 px-4 py-3"
                      data-testid={`social-card-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <s.icon size={16} className="text-silver-dim transition-colors group-hover:text-ember" />
                      <span className="text-left">
                        <span className="block text-xs font-medium text-silver">{s.label}</span>
                        <span className="block text-[10px] text-silver-muted">{s.note}</span>
                      </span>
                    </a>
                  );
                })}
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-10 corner-frame inline-block border border-silver/20 bg-navy/50 p-8">
                <p className="font-display text-2xl italic text-silver">Have a collaboration in mind?</p>
                <Link to="/contact" className="btn-ember mt-5" data-testid="footer-get-in-touch-btn">
                  <span>Get in Touch</span> <ArrowUpRight size={15} />
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 gap-8 self-start">
            <div>
              <p className="eyebrow">Explore</p>
              <ul className="mt-5 space-y-3">
                {QUICK.map((q) => (
                  <li key={q.to}>
                    <Link to={q.to} className="text-sm text-silver-dim transition-colors hover:text-white" data-testid={`footer-quick-${q.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      {q.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="eyebrow">The Club</p>
              <ul className="mt-5 space-y-3 text-sm text-silver-dim">
                <li>Sophia College for Women</li>
                <li>Mumbai, Maharashtra</li>
                <li className="pt-2 text-silver-muted">Our Sponsor Club</li>
                <li className="text-silver-muted">Rotary Club of Bombay Central</li>
                <li className="pt-2 text-silver-muted">Rotary Year 2026–27</li>
                <li className="text-silver-muted">Theme — Forged: Reclaiming the Fire</li>
                <li>
                  <Link to="/login" className="text-[11px] text-silver-muted/70 underline-offset-4 hover:underline" data-testid="footer-member-login-link">
                    Member Login
                  </Link>
                </li>
                <li>
                  <Link to="/admin" className="text-[11px] text-silver-muted/70 underline-offset-4 hover:underline" data-testid="footer-admin-link">
                    Admin Portal
                  </Link>
                </li>
              </ul>
              <div className="mt-8">
                <Logo size={44} withText={false} />
              </div>
            </div>
          </div>
        </div>
        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-silver/10 pt-8 sm:flex-row">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-silver-muted">
            © 2026–27 Rotaract Club of Sophia College for Women
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-silver-muted">Forged · Reclaiming the Fire</p>
        </div>
      </div>
    </footer>
  );
}
